import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {Checkbox} from 'antd';
import styled from 'styled-components';
import dayjs from 'dayjs';
import {t} from 'i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {solid} from '@fortawesome/fontawesome-svg-core/import.macro';
import {Colors} from '@arboxappv4/shared/src/styles/Colors';
import {DB_TIME_FORMAT} from '@arboxappv4/shared/src/constants/constants';
import {getPartialAttendanceDisplay, isSeriesEventFull} from '../../../helpers/functions';
import {getDisabledPagesCourseProps} from '@arboxappv4/shared/src/helpers/DisablePages';

const TrackCourseEventRow = ({seriesEvent, series, onSelect, isDisabled, isChecked}) => {
    const globalLocation = useSelector(state => state.site.selectedLocation);
    const displayProps = useMemo(() => getDisabledPagesCourseProps(series), [series]);
    const dayName = t(`day-${seriesEvent.day_of_week}`);
    const startTime = dayjs(seriesEvent.start, DB_TIME_FORMAT).format(globalLocation?.time_format ?? 'HH:mm');
    const endTime = dayjs(seriesEvent.end, DB_TIME_FORMAT).format(globalLocation?.time_format ?? 'HH:mm');
    const isFull = isSeriesEventFull(series, seriesEvent.id);
    const showCapacity = displayProps.showNumOpenSpots || displayProps.showNumRegistered;
    const isMuted = isDisabled && !isChecked;

    const handleRowClick = () => {
        if (!isDisabled || isChecked) {
            onSelect(series.id, seriesEvent.id);
        }
    };

    return (
        <EventRow onClick={handleRowClick} role="presentation">
            <CheckboxWrap $isMuted={isMuted}>
                <Checkbox
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={() => onSelect(series.id, seriesEvent.id)}
                    onClick={(e) => e.stopPropagation()}
                />
            </CheckboxWrap>
            <DayText $isMuted={isMuted}>{dayName}</DayText>
            <TimeText $isMuted={isMuted}>{startTime}–{endTime}</TimeText>
            {showCapacity &&
                <>
                    <DotSeparator>·</DotSeparator>
                    <CapacityWrap $isFull={isFull}>
                        <UserIcon icon={solid('user')} $isFull={isFull}/>
                        <CapacityText $isFull={isFull}>
                            {getPartialAttendanceDisplay(displayProps, series, seriesEvent.id)}
                        </CapacityText>
                    </CapacityWrap>
                </>
            }
            {isFull && <FullBadge>{t('full')}</FullBadge>}
            <DotSeparator>·</DotSeparator>
            {seriesEvent.description && <DescriptionText $isMuted={isMuted}>{seriesEvent.description}</DescriptionText>}
        </EventRow>
    );
};

export default TrackCourseEventRow;

const EventRow = styled.div`
    display: flex;
    align-items: center;
    padding: 12px 20px;
    background: ${Colors.white};
    &:not(:last-child) {
        border-bottom: 1px solid #f0f0f0;
    }
    cursor: pointer;
    min-height: 45px;
    box-sizing: border-box;
`;

const CheckboxWrap = styled.div`
    padding-inline-end: 12px;
    display: flex;
    align-items: center;
    opacity: ${({$isMuted}) => $isMuted ? 0.5 : 1};

    .ant-checkbox-wrapper {
        margin-inline-start: 0;
    }
`;

const DayText = styled.span`
    font-size: 16px;
    font-weight: 500;
    color: ${Colors.siteBlack};
    line-height: 20px;
    padding-inline-end: 12px;
    opacity: ${({$isMuted}) => $isMuted ? 0.5 : 1};
    white-space: nowrap;
`;

const TimeText = styled.span`
    font-size: 16px;
    font-weight: 400;
    color: ${Colors.siteBlack};
    line-height: 20px;
    padding-inline-end: 6px;
    opacity: ${({$isMuted}) => $isMuted ? 0.5 : 1};
    white-space: nowrap;
`;

const DescriptionText = styled.span`
    font-size: 16px;
    font-weight: 400;
    color: ${Colors.siteBlack};
    line-height: 20px;
    padding-inline-end: 6px;
    opacity: ${({$isMuted}) => $isMuted ? 0.5 : 1};
`;

const DotSeparator = styled.span`
    font-size: 14px;
    color: ${Colors.siteBlack};
    padding: 0 8px;
    line-height: 16px;
`;

const CapacityWrap = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const UserIcon = styled(FontAwesomeIcon)`
    && {
        font-size: 16px;
        width: 12px;
        height: 12px;
        color: ${({$isFull}) => $isFull ? Colors.siteError : Colors.siteBlack};
    }
`;

const CapacityText = styled.span`
    font-size: 16px;
    font-weight: 400;
    line-height: 16px;
    color: ${({$isFull}) => $isFull ? Colors.siteError : Colors.siteBlack};
`;

const FullBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    background: #f1f1f1;
    font-size: 16px;
    font-weight: 400;
    line-height: 16px;
    color: ${Colors.siteBlack};
    white-space: nowrap;
`;
